import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Send, Lock, Check, Dumbbell } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SUGGESTIONS = [
  "Build me a 4-day push/pull/legs split",
  "How do I break a bench press plateau?",
  "What should I eat to lose fat but keep muscle?",
  "Fix my squat form cues",
];

export default function Coach() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (user?.has_coach) api.get("/coach/messages").then((r) => setMessages(r.data));
  }, [user?.has_coach]);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/coach/chat", { message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Coach unavailable");
      setMessages((m) => m.slice(0, -1));
    } finally { setBusy(false); }
  };

  if (!user?.has_coach) {
    return (
      <div className="p-6 md:p-10 max-w-3xl">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#007AFF] mb-2">Pro Feature</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter leading-none mb-8">AI Physical Coach</h1>

        <div className="relative overflow-hidden border border-[#007AFF]/30 bg-[#007AFF]/5 p-10 coach-glow">
          <Lock className="text-[#007AFF] mb-6" size={40} />
          <h2 className="font-display font-black text-3xl uppercase tracking-tight mb-3">Upgrade to Pro</h2>
          <p className="text-white/70 mb-8 max-w-md">The AI coach is part of the <span className="text-[#007AFF] font-semibold">Pro plan ($14.99/mo)</span>. Get tactical, science-based coaching powered by Claude, 24/7.</p>
          <ul className="space-y-3 mb-8">
            {["Personalized workout programming", "Nutrition & recovery guidance", "Form checks and progression advice", "Available 24/7, remembers your chats"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-white/80"><Check size={18} className="text-[#007AFF]" /> {t}</li>
            ))}
          </ul>
          <button data-testid="upgrade-to-pro" onClick={() => navigate("/plans")} className="bg-[#007AFF] hover:bg-[#1a8cff] text-white font-bold uppercase tracking-wide px-8 py-4 flex items-center gap-2 transition-transform duration-150 hover:-translate-y-0.5 shadow-[0_0_20px_rgba(0,122,255,0.4)]">
            <Sparkles size={20} /> See Pro Plan
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] md:h-screen">
      <div className="px-6 md:px-10 py-5 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-[#007AFF]/20 flex items-center justify-center coach-glow"><Sparkles className="text-[#007AFF]" size={20} /></div>
          <div>
            <h1 className="font-display font-black text-2xl uppercase tracking-tight leading-none">Reprise <span className="text-[#007AFF]">Coach</span></h1>
            <span className="text-xs text-white/40">Powered by Claude · Premium</span>
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 md:px-10 py-6" data-testid="coach-messages">
        {messages.length === 0 && (
          <div className="max-w-2xl mx-auto text-center py-10">
            <Dumbbell className="text-[#007AFF] mx-auto mb-4" size={40} />
            <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-2">Ask your coach anything</h2>
            <p className="text-white/40 mb-8">Training plans, nutrition, form, recovery — you name it.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid="coach-suggestion" onClick={() => send(s)} className="text-left p-4 bg-[#121212] border border-white/10 hover:border-[#007AFF]/50 text-sm text-white/70 hover:text-white transition-colors">{s}</button>
              ))}
            </div>
          </div>
        )}

        <div className="max-w-3xl mx-auto space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-4 py-3 whitespace-pre-wrap text-sm leading-relaxed ${m.role === "user" ? "bg-[#FF3B30] text-white" : "bg-[#121212] border border-[#007AFF]/20 text-white/90"}`}>
                {m.content}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="px-4 py-3 bg-[#121212] border border-[#007AFF]/20 flex gap-1.5">
                <span className="h-2 w-2 bg-[#007AFF] animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="h-2 w-2 bg-[#007AFF] animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="h-2 w-2 bg-[#007AFF] animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="px-6 md:px-10 py-4 border-t border-white/10 bg-[#0A0A0A]">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="max-w-3xl mx-auto flex gap-2">
          <input data-testid="coach-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask your coach..." className="flex-1 bg-[#121212] border border-white/10 px-4 py-3 focus:border-[#007AFF] focus:outline-none" />
          <button data-testid="coach-send" disabled={busy} className="bg-[#007AFF] hover:bg-[#1a8cff] disabled:opacity-50 text-white px-5 flex items-center justify-center transition-transform duration-150 hover:-translate-y-0.5"><Send size={18} /></button>
        </form>
      </div>
    </div>
  );
}
